import { DrawerContent } from "@chakra-ui/react";
import React, { memo, useCallback, useMemo, useState } from "react";
import { MessageThreadFragment, MessageThreadType } from "../../gql/graphql";
import DrawerTopHeader from "./drawer-top-header";
import useMessageThreadDraft from "modules/hooks/use-message-thread-draft";
import { FORWARD_DRAFT_ID, FORWARD_TO_STATE } from "modules/utils/Constants";
import ForwardToSelectMessageThread from "./forward-to/forward-to-select-message-thread";
import ForwardToSelectMessages from "./forward-to/forward-to-select-messages";
import ForwardToCompose from "./forward-to/forward-to-compose";
import ForwardToReply from "./forward-to/forward-to-reply";
import MESSAGE_THREAD_FRAGMENT from "modules/messaging/_networking/message-thread-fragment";
import { useApolloClient } from "@apollo/client";
import { useDropzone } from "react-dropzone";
import { useFile } from "modules/providers/file-provider";
import DropFilesHere from "modules/messaging/_shared/drop-files-here";
import { usePanelSize } from "modules/providers/panel-size-provider";

interface IProps {
  initialForwardToState?: FORWARD_TO_STATE;
}

const ForwardMessageThreadDrawer: React.FC<IProps> = ({
  initialForwardToState = "select-messages",
}: IProps) => {
  const { uploadFiles } = useFile();
  const { rightWidth } = usePanelSize();
  const { cache } = useApolloClient();
  const { draft, setForwardToMessageThread, setType } = useMessageThreadDraft({
    draftId: FORWARD_DRAFT_ID,
  });

  const messageThread = useMemo(() => {
    if (!draft?.relatedMessageThread) return null;
    return cache.readFragment<MessageThreadFragment>({
      id: `MessageThread:${draft.relatedMessageThread.id}`,
      fragment: MESSAGE_THREAD_FRAGMENT,
      fragmentName: "MessageThread",
    });
  }, [cache, draft?.relatedMessageThread]);

  const [forwardToState, setForwardToState] = useState<FORWARD_TO_STATE>(
    initialForwardToState,
  );

  const title = useMemo(() => {
    switch (forwardToState) {
      case "select-messages":
        return "Select Messages";
      case "select-forward-to":
        return "Forward to";
      case "compose": {
        switch (draft?.type) {
          case MessageThreadType.Thread:
            return "Forward to Thread";
          case MessageThreadType.Chat:
            return "Forward to Chat";
          case MessageThreadType.Task:
            return "Forward to Task";
          default:
            return "Compose";
        }
      }
      case "reply": {
        if (draft?.forwardToMessageThread) {
          switch (draft?.forwardToMessageThread.type) {
            case MessageThreadType.Thread:
              return "Forward to Thread";
            case MessageThreadType.Chat:
              return "Forward to Chat";
            case MessageThreadType.Task:
              return "Forward to Task";
            default:
              return "Forward";
          }
        }
        return "Compose";
      }
      default:
        return null;
    }
  }, [draft?.forwardToMessageThread, draft?.type, forwardToState]);

  const onForwardToSelectedMessageThread = useCallback(
    (selectedMessageThread: MessageThreadFragment) => {
      setForwardToMessageThread(selectedMessageThread);
      setForwardToState("reply");
    },
    [setForwardToMessageThread],
  );

  const onSelectMessagesNextClicked = useCallback(() => {
    setForwardToState("select-forward-to");
  }, []);

  const onForwardToNewMessageThread = useCallback(
    (type: MessageThreadType) => () => {
      setType(type);
      setForwardToMessageThread(undefined);
      setForwardToState("compose");
    },
    [setForwardToMessageThread, setType],
  );

  const onForwardToChange = useCallback(() => {
    setForwardToMessageThread(undefined);
    setForwardToState("select-forward-to");
  }, [setForwardToMessageThread]);

  const onFileListUpload = useCallback(
    (files: File[]) => uploadFiles(FORWARD_DRAFT_ID, files),
    [uploadFiles],
  );

  const { getRootProps, isDragActive } = useDropzone({
    onDrop: onFileListUpload,
    noClick: true,
  });

  if (!draft || !draft.relatedMessageThread || !messageThread) return null;

  return (
    <DrawerContent {...getRootProps()} w={rightWidth} maxW={rightWidth}>
      <DrawerTopHeader title={title} />

      {forwardToState === "select-messages" && (
        <ForwardToSelectMessages
          messageThread={messageThread}
          onNextClicked={onSelectMessagesNextClicked}
          draftId={FORWARD_DRAFT_ID}
        />
      )}

      {forwardToState === "select-forward-to" && (
        <ForwardToSelectMessageThread
          onForwardToSelectedMessageThread={onForwardToSelectedMessageThread}
          onForwardToNewMessageThread={onForwardToNewMessageThread}
        />
      )}

      {(forwardToState === "compose" || forwardToState === "reply") && (
        <DropFilesHere isDragActive={isDragActive} width={rightWidth} />
      )}

      {forwardToState === "compose" && (
        <ForwardToCompose draftId={FORWARD_DRAFT_ID} />
      )}

      {forwardToState === "reply" && (
        <ForwardToReply
          draftId={FORWARD_DRAFT_ID}
          onForwardToChangeClicked={onForwardToChange}
        />
      )}
    </DrawerContent>
  );
};

export default memo(ForwardMessageThreadDrawer);
